import Icon from "@amzn/meridian/esm/components/icon";
import Button from "@amzn/meridian/esm/components/button";
import Theme from "@amzn/meridian/esm/components/theme";

/**
 * IconButton - a thin, feature-agnostic wrapper around a Meridian icon Button.
 *
 * Meridian's icon SVGs ship at a fixed native size and can't be resized via icon
 * size tokens, so the whole button is scaled down by a scale wrapper div (the
 * pattern the session cards used inline as `styles.smallIcon`, absorbed here).
 * Optional Meridian Theme tokens (e.g. a destructive red treatment) are applied
 * only when provided, so unthemed buttons don't get wrapped in an empty Theme.
 *
 * Shared at the session level so both actionItems/ and agenda/ can consume it
 * (promotion to the nearest common home of its consumers).
 *
 * @param {object}   props
 * @param {object}   props.icon        Meridian icon tokens to render (e.g. pencilTokens).
 * @param {Function} props.onClick     Click handler for the button.
 * @param {string}   props.ariaLabel   REQUIRED accessible label for the icon-only button.
 * @param {string}   [props.size]      Meridian Button size ("small" default).
 * @param {object}   [props.tokens]    Optional Meridian Theme tokens; wraps the button in
 *                                     <Theme> only when supplied.
 * @param {object}   [props.style]     Optional extra style merged onto the scale wrapper div
 *                                     (callers use it for hover-reveal opacity/pointerEvents).
 */
const IconButton = ({
  icon,
  onClick,
  ariaLabel,
  size = "small",
  tokens,
  style,
}) => {
  // Scale the whole icon button down (absorbs the former styles.smallIcon = scale(0.6)); merge
  // any caller-supplied style (e.g. the hover-reveal controlsStyle) on top.
  const button = (
    <div style={{ transform: "scale(0.6)", ...style }}>
      <Button
        type="icon"
        size={size}
        onClick={onClick}
        aria-label={ariaLabel}
      >
        <Icon tokens={icon} />
      </Button>
    </div>
  );

  // Only wrap in Theme when tokens are provided so unthemed buttons don't get an empty Theme.
  return tokens ? <Theme tokens={tokens}>{button}</Theme> : button;
};

export default IconButton;
